import type { PayloadAction } from '@reduxjs/toolkit';

import { createSlice } from '@reduxjs/toolkit';

import type { IWebUploadDetail, IWebUploadRow } from 'src/types/web-upload';

export interface IWebUploadsState {
  isLoading: boolean;
  hasError: boolean;
  error: string;
  rows: IWebUploadRow[];
  page: number;
  rowsPerPage: number;
  totalCount: number | null;
  hasNext: boolean;
  currentUpload: IWebUploadDetail | null;
  detailLoading: boolean;
  detailHasError: boolean;
  detailError: string;
}

const initialState: IWebUploadsState = {
  isLoading: false,
  hasError: false,
  error: '',
  rows: [],
  page: 0,
  rowsPerPage: 30,
  totalCount: null,
  hasNext: false,
  currentUpload: null,
  detailLoading: false,
  detailHasError: false,
  detailError: '',
};

type SetWebUploadsPagePayload = {
  rows: IWebUploadRow[];
  page: number;
  hasNext: boolean;
  totalCount?: number;
};

type SetWebUploadDetailPayload = {
  currentUpload: IWebUploadDetail | null;
};

export const webUploadsSlice = createSlice({
  name: 'web-uploads',
  initialState,
  reducers: {
    startLoading: (state) => {
      state.isLoading = true;
      state.hasError = false;
      state.error = '';
    },
    hasError: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.hasError = true;
      state.error = action.payload;
    },
    stopLoading: (state) => {
      state.isLoading = false;
    },
    setWebUploadsPage(state, action: PayloadAction<SetWebUploadsPagePayload>) {
      state.rows = action.payload.rows;
      state.page = action.payload.page;
      state.hasNext = action.payload.hasNext;
      if (action.payload.totalCount !== undefined) {
        state.totalCount = action.payload.totalCount;
      }
      state.isLoading = false;
    },
    setWebUploadsTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setWebUploadsRowsPerPage(state, action: PayloadAction<number>) {
      state.rowsPerPage = action.payload;
    },
    clearWebUploads: (state) => {
      state.rows = [];
      state.page = 0;
      state.hasNext = false;
      state.totalCount = null;
      state.isLoading = false;
      state.hasError = false;
      state.error = '';
    },
    startDetailLoading: (state) => {
      state.detailLoading = true;
      state.detailHasError = false;
      state.detailError = '';
      state.currentUpload = null;
    },
    setWebUploadDetail(state, action: PayloadAction<SetWebUploadDetailPayload>) {
      state.currentUpload = action.payload.currentUpload;
      state.detailLoading = false;
    },
    clearWebUploadDetail: (state) => {
      state.currentUpload = null;
      state.detailLoading = false;
      state.detailHasError = false;
      state.detailError = '';
    },
    setWebUploadDetailError: (state, action: PayloadAction<string>) => {
      state.detailLoading = false;
      state.detailHasError = true;
      state.detailError = action.payload;
    },
  },
});

export const {
  startLoading,
  stopLoading,
  hasError,
  setWebUploadsPage,
  setWebUploadsTotalCount,
  setWebUploadsRowsPerPage,
  clearWebUploads,
  startDetailLoading,
  setWebUploadDetail,
  clearWebUploadDetail,
  setWebUploadDetailError,
} = webUploadsSlice.actions;

export default webUploadsSlice.reducer;
